import { type IconType } from '@/defs/icon'
import { type ColorIdType, type ColorType, type RarityType } from '@/defs/mtg'
import { type PriceSourceMapType } from '@/defs/price'

export const CARD_ID_DELIMITER = ':|:' as const

declare const CARD_ID: unique symbol
export type CardIdType = string & { readonly [CARD_ID]: true }

export type CardRefType = {
  cardId: CardIdType
  name: string
}

export type CardFaceOrientationType =
  | 'normal'
  | 'aftermathFirst'
  | 'aftermathSecond'
  | 'battleFront'
  | 'battleBack'
  | 'flippableNormal'
  | 'flippableFlipped'
  | 'meldable'
  | 'melded'
  | 'modalFront'
  | 'modalBack'
  | 'split'
  | 'transformFront'
  | 'transformBack'

export const CARD_FACE_ORIENTATION_LABELS: Record<
  CardFaceOrientationType,
  { icon: IconType; isBack?: true; label: string; rotation?: '90' | '180' | '270' }
> = {
  aftermathFirst: { icon: 'rotateLeft', label: 'Rotate' },
  aftermathSecond: { icon: 'rotateRight', label: 'Rotate', rotation: '270' },
  battleBack: { icon: 'rotateLeft', isBack: true, label: 'Transform' },
  battleFront: { icon: 'rotateRight', label: 'Transform', rotation: '90' },
  flippableFlipped: { icon: 'rotateLeft', label: 'Rotate', rotation: '180' },
  flippableNormal: { icon: 'rotateRight', label: 'Rotate' },
  meldable: { icon: 'merge', label: 'Meld' },
  melded: { icon: 'split', isBack: true, label: 'Unmeld' },
  modalBack: { icon: 'arrowsRotate', isBack: true, label: 'Turn Over' },
  modalFront: { icon: 'arrowsRotateReverse', label: 'Turn Over' },
  normal: { icon: 'circleXmark', label: 'Reset' },
  split: { icon: 'circleXmark', label: 'Reset', rotation: '90' },
  transformBack: { icon: 'arrowsRotate', isBack: true, label: 'Transform' },
  transformFront: { icon: 'arrowsRotateReverse', label: 'Transform' },
}

export const CARD_PART_COMPONENT_SLUGS = ['meld_part', 'meld_result', 'combo_piece'] as const
export type CardPartComponentSlugType = (typeof CARD_PART_COMPONENT_SLUGS)[number]
export const CARD_PART_COMPONENT_LABELS: Record<CardPartComponentSlugType, { label: string; labelPlural?: string }> = {
  combo_piece: { label: 'Related Card', labelPlural: 'Related Cards' },
  meld_part: { label: 'Melds With' },
  meld_result: { label: 'Melds Into' },
}
export const isCardPartComponentSlugType = (slug?: string): slug is CardPartComponentSlugType =>
  CARD_PART_COMPONENT_SLUGS.includes(slug as CardPartComponentSlugType)

export type CardPartType = CardRefType & {
  component: CardPartComponentSlugType
}

export type CardType = {
  allParts?: CardPartType[]
  cmc?: number
  colorId: ColorIdType
  colors?: ColorType[]
  flavorCardRefs?: CardRefType[]
  hasContentWarning?: boolean
  image?: string
  imageArt?: string
  imageBack?: string
  isBackground?: boolean
  isBackgroundChooser?: boolean
  isBanned?: boolean
  isDoctor?: boolean
  isDoctorsCompanion?: boolean
  isGameChanger?: boolean
  isLegalCommander?: boolean
  isLegalCompanion?: boolean
  isLegalPartner?: boolean
  isNew?: boolean
  isUniversesBeyond?: boolean
  isUnofficial?: boolean
  layout?: string
  manaCost?: string
  name: string
  nameBack?: string
  nameFront?: string
  oracleText?: string
  oracleTextBack?: string
  originalCardRef?: CardRefType
  partnerVariant?: string
  partnersWith?: string
  power?: number
  precon?: string
  priceSources?: PriceSourceMapType
  primaryType?: string
  rarity?: RarityType
  releasedAt?: string
  salt?: number
  scryfallId?: string
  scryfallUri?: string
  sets?: string[]
  spellbookUri?: string
  spoiledAt?: string
  tags?: string[]
  toughness?: number
  type?: string
  typeline?: string
  typelineBack?: string
  types?: string[]
  uniqueArtwork?: {
    artist?: string
    collectorNumber?: string
    image?: string
    imageBack?: string
    scryfallId?: string
    set?: string
    setName?: string
  }[]
}

export type CardMapType = Partial<Record<CardIdType, CardType>>
